// src/api/queries/production-calendar.js
/**
 * Хуки производственного календаря. GET /calendar — массив
 * ProductionCalendarYearDto { year, holidays: ISO[], shortDays: ISO[] }
 * (один SELECT по годам + один по дням на сервере, без N+1).
 *
 * Мутации: POST /:year (создать год), DELETE /:year, POST /:year/days
 * { kind, dates }, DELETE /:year/days/:date. Ответ — обновлённый год
 * (ProductionCalendarYearDto, по ТЗ Шага 14), но фронт его не использует:
 * onSuccess инвалидирует ["production-calendar"], вызывающий код делает
 * refetch GET /calendar.
 *
 * kind: фронт (ProductionCalendarView, KIND_META) работает с plural-ключами
 * 'holidays' / 'shortDays' (это UI-ключи для двух секций). Бэк ждёт singular
 * 'holiday' / 'shortday' (AddCalendarDaysSchema.kind = z.enum(['holiday',
 * 'shortday'])) — маппинг в useAddCalendarDaysMutation.mutationFn.
 * В остальных хуках kind не участвует (DELETE /:year/days/:date body не
 * принимает).
 *
 * GET /:year отдельным хуком не обёрнут: view берёт данные из общего
 * списка getAll (уже с деталями). Если понадобится per-year refetch —
 * добавить useProductionCalendarYearQuery(year) с ключом
 * ["production-calendar", "year", year].
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export const CALENDAR_KEY = ["production-calendar"];

export function useProductionCalendarQuery(options = {}) {
  return useQuery({
    queryKey: CALENDAR_KEY,
    queryFn: () => api.get("/calendar"),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useAddCalendarYearMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year) => api.post(`/calendar/${year}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEY }),
  });
}

export function useRemoveCalendarYearMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year) => api.delete(`/calendar/${year}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEY }),
  });
}

export function useAddCalendarDaysMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ year, kind, dates }) => {
      const apiKind = kind === "holidays" ? "holiday" : "shortday";
      return api.post(`/calendar/${year}/days`, { kind: apiKind, dates });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEY }),
  });
}

export function useRemoveCalendarDayMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ year, date }) =>
      api.delete(`/calendar/${year}/days/${date}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEY }),
  });
}
